import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Autocomplete,
  DialogActions,
  DialogContent,
  DialogContentText,
  InputAdornment,
  MenuItem,
  Tooltip
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import moment from 'moment';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { ButtonComponent } from 'src/components/common/ButtonComponent';
import Loader from 'src/components/common/Loader';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { useUser } from 'src/components/hooks/useUser';
import { SERVER_DATE_FORMAT } from 'src/constants/formats';
import { setMessage } from 'src/store/Message';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import { formatInput } from 'src/util/STR';
import { useSelectedAppInfo } from 'src/util/SelectedAppInfo';
import { updateUserAccess } from 'src/util/util';
import { useFieldTranslation } from '../../fields/useFieldTranslation';
import AssociatedDocuments from './AssociatedDocuments';
import Progress from './Progress';
import { useAMilestoneDetailModal } from './useMilestoneDetailModal';
import { useMilestoneTracking } from './useMilestoneTracking';
import { showLoader, hideLoader } from 'src/store/Loader';
import dayjs from 'dayjs';
import { LoadingButton } from '@mui/lab';

export interface IFilterActionTypes {
  all: boolean;
  dataEntry: boolean;
  dataChange: boolean;
  fieldQuery: boolean;
  eSign: boolean;
  fromDate: boolean;
  toDate: boolean;
  users: boolean;
}
export interface IFilterObject {
  filterProps: IFilterActionTypes;
  filterFromDate: string | null;
  filterToDate: string | null;
  filterUsers: string[];
}

const BootstrapDialogTitle = (props: { [x: string]: any; children: any; onClose: any }) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};
BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`
  };
}

export interface ICollapseItem {
  [key: string]: boolean;
}
export const expandAllTxt = 'EXPAND ALL';
export const collapseAllTxt = 'COLLAPSE ALL';

export const MilestoneDetailModal: React.FunctionComponent<any> = ({
  gridRows,
  selectedRowId,
  handleClose,
  getData,
  auditUsersList,
  isGridDataLoading,
  openDetailsModal
}) => {
  const [selectedMilestone, setSelectedMileStone] = useState<any>(
    gridRows.filter((item: any) => item.milestone_id === selectedRowId)[0]
  );
  const { selectedAppSection } = useSelectedAppInfo();
  const [updated, setUpdated] = useState(false);
  const dispatchApp = useAppDispatch();
  const dispatch = useAppDispatch();
  const { checkPermission } = usePermissionWrapper();
  const { user } = useUser();
  const {
    mileStoneDateUpdateValidationMsg,
    mileStoneInvalidDateValidationMsg,
    mileStoneDataUpdatedMsg,
    mileStoneErrorMsg,
    unSavedWarning
  } = useFieldTranslation();

  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const { preferedFormatForDatePicker, preferedDateFormat, userPreferedDateFormat } = usePreferedDateFormat();
  const { updateMilestoneDataStatus, updateMilestoneSuccess } = useMilestoneTracking();
  const exportDateFormat = userPreferedDateFormat.includes('/')
    ? userPreferedDateFormat.replaceAll('/', '_')
    : userPreferedDateFormat.replaceAll('-', '_');

  const {
    auditFilterData,
    auditFilterError,
    getauditFilterData,
    updateMilestone,
    updateMilestoneStartPlannedDates,
    updateMilestoneEndPlannedDates,
    updateStartPlanneddateSuccess,
    updateEndPlanneddateSuccess,
    reactivateMilestone,
    reactivateMilestoneError,
    reactivateMilestoneSucess,
    reactivateMilestoneDataIsError,
    updateActualDates
  } = useAMilestoneDetailModal();

  const [value, setValue] = React.useState(0);
  const handleTabsChange = (event: any, newValue: React.SetStateAction<number>) => {
    if (
      newValue !== 0 &&
      (editStartDatePlanned ||
        editEndDatePlanned ||
        editStartDateAdjusted ||
        editEndDateAdjusted ||
        editStartDateActual ||
        editEndDateActual ||
        editAssignedTo) &&
      isUpdate
    ) {
      setNextTabValue(newValue);
      setOpenConfirmExit(true);
    } else if (newValue === 3 && event.currentTarget.innerText === 'AUDIT TRAIL') {
      getAuditHistory(
        selectedMilestone.milestone_id,
        0,
        maxSizeOfAuditDetails,
        sortBy == 'Newest' ? 'desc' : 'asc',
        audiFilterDataObj
      );
      setValue(newValue);
    } else {
      setValue(newValue);
    }
    setUpdatableFieldsToFalse();
  };

  const [editStartDateAdjusted, setEditStartDateAdjusted] = useState(false);
  const [editStartDatePlanned, setEditStartDatePlanned] = useState(false);
  const [editEndDatePlanned, setEditEndDatePlanned] = useState(false);
  const [comments, setComments] = useState(selectedMilestone.milestone_comments);
  const [editEndDateAdjusted, setEditEndDateAdjusted] = useState(false);
  const [editEndDateActual, setEditEndDateActual] = useState(false);
  const [editStartDateActual, setEditStartDateActual] = useState(false);
  const [editAssignedTo, setEditAssignedTo] = useState(false);
  const [endDateAdjusted, setEndDateAdjusted] = useState(selectedMilestone.end_date_adjusted);
  const [milestoneStatus, setMilestoneStatus] = useState(selectedMilestone.milestone_status);
  const [assignedTo, setAssignedTo] = useState(selectedMilestone.assigned_to);
  const [updatedAssignedTo, setupdatedAssignedTo] = useState<any>({ updated_by: user });
  const commentRef = useRef<any>();
  const assignedToRef = useRef<any>();
  const [startDateAdjusted, setStartDateAdjusted] = useState<any>(selectedMilestone.start_date_adjusted);
  const [startDateActual, setStartDateActual] = useState<any>(selectedMilestone.start_date_actual);
  const [endDateActual, setEndDateActual] = useState<any>(selectedMilestone.end_date_actual);
  const [startDatePlanned, setStartDatePlanned] = useState<any>(selectedMilestone.start_date_planned_baseline);
  const [endDatePlanned, setEndDatePlanned] = useState<any>(selectedMilestone.end_date_planned_baseline);
  const [updatedStartDateAdjusted, setUpdatedStartDateAdjusted] = useState<any>({
    start_date_adjusted: selectedMilestone.start_date_adjusted,
    updated_by: user
  });

  const [updatedEndDateAdjusted, setUpdatedEndDateAdjusted] = useState<any>({
    end_date_adjusted: selectedMilestone.end_date_adjusted,
    updated_by: user
  });
  const [updatedStartDateActual, setUpdatedStartDateActual] = useState<any>({
    start_date_actual: selectedMilestone.start_date_actual,
    updated_by: user
  });
  const [updatedEndDateActual, setUpdatedEndDateActual] = useState<any>({
    end_date_actual: selectedMilestone.end_date_actual,
    updated_by: user
  });
  const [updatedStartDatePlanned, setUpdatedStartDatePlanned] = useState<any>({
    start_date_planned_baseline: selectedMilestone.start_date_planned_baseline
  });
  const [updatedEndDatePlanned, setUpdatedEndDatePlanned] = useState<any>({
    end_date_planned_baseline: selectedMilestone.end_date_planned_baseline
  });
  const [auditTrailData, setAuditTrailData] = useState<any>([]);
  const [noOfAuditResults, setNoOfAuditResults] = useState(0);
  const perPage = 10;
  const [page, setPage] = useState(1);
  const [paginatedAuditDetails, setPaginatedAuditDetails] = React.useState<any>({});
  const [loadingStartDA, setLoadingStartDA] = useState(false);
  const [loadingEndDA, setLoadingEndDA] = useState(false);
  const [loadingStartDateActual, setLoadingStartDateActual] = useState(false);
  const [loadingEndDateActual, setLoadingEndDateActual] = useState(false);
  const [loadingStartDB, setLoadingStartDB] = useState(false);
  const [loadingEndDB, setLoadingEndDB] = useState(false);
  const [loadingAssignedTo, setLoadingAssignedTo] = useState(false);
  const isActivated =
    !selectedMilestone.is_activated ||
    selectedMilestone.start_date_planned_baseline === null ||
    selectedMilestone.end_date_planned_baseline === null;
  const plannedDateConditions =
    checkPermission('update_milestones_planned_dates') &&
    isActivated &&
    selectedMilestone.milestone_status !== 'Planned';
  const handlePagination = (_: React.ChangeEvent<unknown>, pageValue: number) => {
    if (pageValue !== page) {
      setPaginationWithGroupBy(auditTrailData, pageValue);
    }
    setPage(pageValue);
  };
  const setPaginationWithGroupBy = (allAuditHistory: any[], pageNumber: number) => {
    if (allAuditHistory) {
      const begin = (pageNumber - 1) * perPage;
      const paginatedResponse = allAuditHistory.slice(begin, begin + perPage);
      setPaginatedAuditDetails(groupBy(paginatedResponse));
    } else {
      setPaginatedAuditDetails(groupBy(allAuditHistory));
    }
  };
  const groupBy = (allData: any) => {
    const listOfMilestoneUpdates: any = {};
    allData.length > 0 &&
      allData.forEach((item: any) => {
        const date = item.date_time_of_change.split('T')[0];
        if (listOfMilestoneUpdates[date]) {
          listOfMilestoneUpdates[date].push(item);
        } else {
          listOfMilestoneUpdates[date] = [item];
        }
      });
    return listOfMilestoneUpdates;
  };

  const [audiFilterDataObj, setAuditFilterDataObj] = useState<any>([]);
  const filterPropsQueries: any = {
    all: true,
    dataEntry: true,
    dataChange: true,
    fieldQuery: true,
    fromDate: false,
    toDate: false,
    users: false
  };
  const initialFilterObject = {
    filterProps: filterPropsQueries,
    filterFromDate: null,
    filterToDate: null,
    filterUsers: []
  };
  const [updatedFilterObject, setUpdatedFilterObject] = useState<IFilterObject>(initialFilterObject);
  const [appliedFilterObject, setAppliedFilterObject] = useState<IFilterObject>(initialFilterObject);
  const [sortBy, setSortBy] = React.useState('Newest');
  const maxSizeOfAuditDetails = 1000;

  const handleFilterApply = () => {
    let filterCount = 0;
    const tempFieldFilters: any[] = [];
    if (
      updatedFilterObject.filterProps.all ||
      updatedFilterObject.filterProps.dataChange ||
      updatedFilterObject.filterProps.dataEntry ||
      updatedFilterObject.filterProps.fieldQuery
    ) {
      if (updatedFilterObject.filterFromDate != 'Invalid date' && updatedFilterObject.filterToDate != 'Invalid date') {
        const isToDateValid =
          updatedFilterObject.filterFromDate !== null && updatedFilterObject.filterToDate !== null
            ? moment(updatedFilterObject.filterToDate) >= moment(updatedFilterObject.filterFromDate)
            : true;
        if (isToDateValid) {
          if (updatedFilterObject.filterProps.all) {
            filterCount = 0;
          } else if (
            updatedFilterObject.filterProps.dataChange ||
            updatedFilterObject.filterProps.dataEntry ||
            updatedFilterObject.filterProps.fieldQuery
          ) {
            filterCount = filterCount + 1;
            const dataChangedArr: string[] = [];
            if (updatedFilterObject.filterProps.dataChange) {
              dataChangedArr.push('Data Update');
            }
            if (updatedFilterObject.filterProps.dataEntry) {
              dataChangedArr.push('Data Entry');
            }
            if (updatedFilterObject.filterProps.fieldQuery) {
              dataChangedArr.push('Field Query-Query Raised');
              dataChangedArr.push('Field Query-Query Deleted');
              dataChangedArr.push('Field Query-Query Forwarded');
              dataChangedArr.push('Field Query-Query Resolved');
              dataChangedArr.push('Field Query-Query Responded');
            }
            tempFieldFilters.push({
              name: 'reason_data_changed',
              value: dataChangedArr,
              operation: 'in'
            });
          }
          if (updatedFilterObject.filterProps.fromDate || updatedFilterObject.filterProps.toDate) {
            filterCount = filterCount + 1;
            if (updatedFilterObject.filterFromDate) {
              if (
                updatedFilterObject.filterProps.dataChange ||
                updatedFilterObject.filterProps.dataEntry ||
                updatedFilterObject.filterProps.fieldQuery
              ) {
                tempFieldFilters.push({
                  name: 'date_time_of_change',
                  value: updatedFilterObject.filterFromDate,
                  operation: 'greater_than_or_equal'
                });
              }
            }
            if (updatedFilterObject.filterToDate) {
              if (
                updatedFilterObject.filterProps.dataChange ||
                updatedFilterObject.filterProps.dataEntry ||
                updatedFilterObject.filterProps.fieldQuery
              ) {
                tempFieldFilters.push({
                  name: 'date_time_of_change',
                  value: updatedFilterObject.filterToDate,
                  operation: 'less_than_or_equal'
                });
              }
            }
          }
          if (updatedFilterObject.filterProps.users) {
            if (updatedFilterObject.filterUsers.length) {
              filterCount = filterCount + 1;
              if (
                updatedFilterObject.filterProps.dataChange ||
                updatedFilterObject.filterProps.dataEntry ||
                updatedFilterObject.filterProps.fieldQuery
              ) {
                tempFieldFilters.push({
                  name: 'audit_user_name',
                  value: updatedFilterObject.filterUsers,
                  operation: 'in'
                });
              }
            }
          }
          setAppliedFilterObject(updatedFilterObject);
          setAppliedFilterCount(filterCount);
          setAuditFilterDataObj(tempFieldFilters);
          setOpenFilterSearch(false);
          getAuditHistory(
            selectedMilestone.milestone_id,
            0,
            maxSizeOfAuditDetails,
            sortBy == 'Newest' ? 'desc' : 'asc',
            tempFieldFilters
          );
        } else {
          dispatchApp(setMessage({ msgDetails: mileStoneDateUpdateValidationMsg, severity: 'error' }));
        }
      } else {
        dispatchApp(setMessage({ msgDetails: mileStoneInvalidDateValidationMsg, severity: 'error' }));
      }
    } else {
      filterCount = filterCount + 1;
      setAppliedFilterObject(updatedFilterObject);
      setAppliedFilterCount(filterCount);
      setAuditFilterDataObj(tempFieldFilters);
      setOpenFilterSearch(false);
      setPage(1);
      setNoOfAuditResults(0);
      setAuditTrailData([]);
      setPaginationWithGroupBy([], 1);
    }
  };
  const handleFilterClear = () => {
    setAuditFilterDataObj([]);
    setUpdatedFilterObject(initialFilterObject);
    setFilterText('');
    setAppliedFilterCount(0);
  };

  useEffect(() => {
    setSelectedMileStone(gridRows.filter((item: any) => item.milestone_id === selectedRowId)[0]);
  }, [gridRows]);

  useEffect(() => {
    setMilestoneStatus(selectedMilestone.milestone_status);
    setStartDatePlanned(selectedMilestone.start_date_planned_baseline);
    setEndDatePlanned(selectedMilestone.end_date_planned_baseline);
  }, [
    selectedMilestone,
    selectedMilestone.start_date_planned_baseline,
    selectedMilestone.end_date_planned_baseline,
    selectedMilestone.start_date_actual,
    selectedMilestone.end_date_actual
  ]);

  useEffect(() => {
    if (auditFilterData) {
      setPage(1);
      setNoOfAuditResults(auditFilterData.length);
      setAuditTrailData(auditFilterData);
      setPaginationWithGroupBy(auditFilterData, 1);
    }
  }, [auditFilterData]);

  useEffect(() => {
    if (auditFilterError) {
      updateUserAccess(auditFilterError, dispatch);
      setPaginatedAuditDetails([]);
    }
  }, [auditFilterError]);

  const getAuditHistory = async (
    recordId: string,
    startFrom: number,
    maxSize: number,
    sortOrder: string,
    filterData: any[]
  ) => {
    if (recordId != undefined && recordId != '') {
      getauditFilterData({
        audit_module_section: 'MILESTONE TRACKING',
        row_id: recordId,
        isOnlyUpdate: false,
        startFrom,
        maxSize,
        sortbyColumn: 'date_time_of_change',
        sortOrder,
        data: filterData,
        site_id: selectedSite,
        study_id: studyId,
        country: selectedCountry,
        application: process.env.REACT_APP_NAME,
        application_section: selectedAppSection
      });
    }
  };

  useEffect(() => {
    if (filterText !== '') {
      setFilteredAuditList(filterText);
    } else {
      getAuditHistory(
        selectedMilestone.milestone_id,
        0,
        maxSizeOfAuditDetails,
        sortBy == 'Newest' ? 'desc' : 'asc',
        audiFilterDataObj
      );
    }
  }, [sortBy]);

  useEffect(() => {
    if (reactivateMilestoneDataIsError) {
      const dataErorr: any = reactivateMilestoneError;
      if (dataErorr?.status == 500) {
        dispatch(
          setMessage({
            msgDetails: `${dataErorr?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (dataErorr?.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      } else if (dataErorr?.status !== 404) {
        dispatch(
          setMessage({
            msgDetails: 'Error Data',
            severity: 'error'
          })
        );
      }
    }
  }, [reactivateMilestoneDataIsError]);

  const handleUsersChange = (_: any, values: any) => {
    setUpdatedFilterObject({
      ...updatedFilterObject,
      filterProps: {
        ...updatedFilterObject.filterProps,
        users: true
      },
      filterUsers: values
    });
  };

  const handleActionTypeChange = (event: any, actionType: string) => {
    const value = event.target.checked;
    let checkFilterObj = { ...updatedFilterObject };
    if (actionType === 'All') {
      checkFilterObj = {
        ...updatedFilterObject,
        filterProps: {
          ...updatedFilterObject.filterProps,
          all: value,
          dataChange: value,
          dataEntry: value,
          fieldQuery: value
        }
      };
    } else {
      checkFilterObj = {
        ...updatedFilterObject,
        filterProps: {
          ...updatedFilterObject.filterProps,
          all: false,
          [actionType]: value
        }
      };
      if (
        checkFilterObj.filterProps.dataChange &&
        checkFilterObj.filterProps.dataEntry &&
        checkFilterObj.filterProps.dataChange &&
        checkFilterObj.filterProps.dataEntry &&
        checkFilterObj.filterProps.fieldQuery
      ) {
        checkFilterObj = {
          ...checkFilterObj,
          filterProps: {
            ...checkFilterObj.filterProps,
            all: true
          }
        };
      }
    }
    setUpdatedFilterObject(checkFilterObj);
  };

  const handleFilterDateChange = (dateValue: any, fromOrTo: string) => {
    if (fromOrTo === 'filterFromDate') {
      setUpdatedFilterObject({
        ...updatedFilterObject,
        filterProps: {
          ...updatedFilterObject.filterProps,
          fromDate: dateValue !== null ? true : false
        },
        filterFromDate:
          dateValue !== null
            ? moment(new Date(dateValue).toLocaleDateString('en-CA')).format('YYYY-MM-DD 00:00:00')
            : dateValue
      });
    } else {
      setUpdatedFilterObject({
        ...updatedFilterObject,
        filterProps: {
          ...updatedFilterObject.filterProps,
          toDate: dateValue !== null ? true : false
        },
        filterToDate:
          dateValue !== null
            ? moment(new Date(dateValue).toLocaleDateString('en-CA')).format('YYYY-MM-DD 23:59:59')
            : dateValue
      });
    }
  };

  const handleSortbyChange = (e: any) => {
    setSortBy(e.target.value);
  };

  const handleReactiveMilestone = async () => {
    await reactivateMilestone({
      study_id: studyId,
      country: selectedCountry,
      site_id: selectedSite,
      application: process.env.REACT_APP_NAME,
      milestone_id: selectedMilestone.milestone_id,
      application_section: selectedAppSection
    });
  };
  /**
     checking different validation scenerios for Start Date Adjusted
      */
  const handleStartDateAdjusted = async () => {
    if (updatedStartDateAdjusted.start_date_adjusted === 'Invalid date') {
      dispatchApp(setMessage({ msgDetails: 'Invalid Date', severity: 'error' }));
    } else if (startDateAdjusted > endDateAdjusted) {
      dispatchApp(
        setMessage({
          msgDetails: 'Start Date Adjusted cannot be greater than End Date Adjusted.',
          severity: 'error'
        })
      );
    } else if (selectedMilestone.start_date_adjusted !== updatedStartDateAdjusted.start_date_adjusted) {
      setLoadingStartDA(true);
      const updateMilestoneRes: any = await updateMilestone({
        study_id: studyId,
        country: selectedCountry,
        site_id: selectedSite,
        application: process.env.REACT_APP_NAME,
        milestone_id: selectedMilestone.milestone_id,
        data: updatedStartDateAdjusted,
        application_section: selectedAppSection
      });
      if (updateMilestoneRes?.data) {
        setEditStartDateAdjusted(false);
        setStartDateAdjusted(updatedStartDateAdjusted.start_date_adjusted);
        setUpdated(true);
        setIsUpdate(false);
        await getData();
        dispatchApp(setMessage({ msgDetails: 'Milestone updated successfully', severity: 'success' }));
      } else if (updateMilestoneRes?.error) {
        if (updateMilestoneRes?.error.status === 403) {
          dispatchApp(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
        } else if (updateMilestoneRes?.error.status === 401) {
          dispatchApp(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
        } else {
          dispatchApp(setMessage({ msgDetails: mileStoneErrorMsg, severity: 'error' }));
        }
      }
    } else {
      setEditStartDateAdjusted(false);
    }
    setLoadingStartDA(false);
  };

  const handleStartDateActual = async () => {
    if (updatedStartDateActual.start_date_actual === 'Invalid date') {
      dispatchApp(setMessage({ msgDetails: 'Invalid Date', severity: 'error' }));
    } else if (new Date(startDateActual) > new Date()) {
      dispatchApp(
        setMessage({
          msgDetails: 'Start Date Actual cannot be Future Date.',
          severity: 'error'
        })
      );
    } else if (selectedMilestone.start_date_actual !== updatedStartDateActual.start_date_actual) {
      setLoadingStartDateActual(true);
      const updateActualDatesRes: any = await updateActualDates({
        study_id: studyId,
        country: selectedCountry,
        site_id: selectedSite,
        application: process.env.REACT_APP_NAME,
        milestone_id: selectedMilestone.milestone_id,
        data: updatedStartDateActual,
        application_section: selectedAppSection
      });
      if (updateActualDatesRes?.data) {
        setEditStartDateActual(false);
        setStartDateActual(updatedStartDateActual.start_date_actual);
        setUpdated(true);
        setIsUpdate(false);
        await getData();
        dispatchApp(setMessage({ msgDetails: 'Milestone updated successfully', severity: 'success' }));
      } else if (updateActualDatesRes?.error) {
        if (updateActualDatesRes?.error.status === 403) {
          dispatchApp(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
        } else if (updateActualDatesRes?.error.status === 401) {
          dispatchApp(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
        } else {
          dispatchApp(setMessage({ msgDetails: mileStoneErrorMsg, severity: 'error' }));
        }
      }
    } else {
      setEditStartDateActual(false);
    }
    setLoadingStartDateActual(false);
  };

  const handleEndDateActual = async () => {
    if (updatedEndDateActual.end_date_actual === 'Invalid date') {
      dispatchApp(setMessage({ msgDetails: 'Invalid Date', severity: 'error' }));
    } else if (startDateActual > endDateActual) {
      dispatchApp(
        setMessage({
          msgDetails: 'End Date Actual cannot be less than Start Date Actual.',
          severity: 'error'
        })
      );
    } else if (new Date(endDateActual) > new Date()) {
      dispatchApp(
        setMessage({
          msgDetails: 'End Date Actual cannot be Future Date.',
          severity: 'error'
        })
      );
    } else if (selectedMilestone.end_date_actual !== updatedEndDateActual.end_date_actual) {
      setLoadingEndDateActual(true);
      const updateActualDatesRes: any = await updateActualDates({
        study_id: studyId,
        country: selectedCountry,
        site_id: selectedSite,
        application: process.env.REACT_APP_NAME,
        milestone_id: selectedMilestone.milestone_id,
        data: updatedEndDateActual,
        application_section: selectedAppSection
      });
      if (updateActualDatesRes?.data) {
        setEditEndDateActual(false);
        setEndDateActual(updatedEndDateActual.end_date_actual);
        setUpdated(true);
        setIsUpdate(false);
        await getData();
        dispatchApp(setMessage({ msgDetails: 'Milestone updated successfully', severity: 'success' }));
      } else if (updateActualDatesRes?.error) {
        if (updateActualDatesRes?.error.status === 403) {
          dispatchApp(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
        } else if (updateActualDatesRes?.error.status === 401) {
          dispatchApp(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
        } else {
          dispatchApp(setMessage({ msgDetails: mileStoneErrorMsg, severity: 'error' }));
        }
      }
    } else {
      setEditEndDateActual(false);
    }
    setLoadingEndDateActual(false);
  };
  /**End */

  /**
     checking different validation scenerios for End Date Adjusted
      */
  const handleEndDateAdjusted = async () => {
    if (updatedEndDateAdjusted.end_date_adjusted === 'Invalid date') {
      dispatchApp(setMessage({ msgDetails: 'Invalid Date', severity: 'error' }));
    } else if (endDateAdjusted < startDateAdjusted) {
      dispatchApp(
        setMessage({
          msgDetails: 'End Date Adjusted cannot be less than Start Date Adjusted',
          severity: 'error'
        })
      );
    } else if (selectedMilestone.end_date_adjusted !== updatedEndDateAdjusted.end_date_adjusted) {
      setLoadingEndDA(true);
      const updateMilestoneRes: any = await updateMilestone({
        study_id: studyId,
        country: selectedCountry,
        site_id: selectedSite,
        application: process.env.REACT_APP_NAME,
        milestone_id: selectedMilestone.milestone_id,
        data: updatedEndDateAdjusted,
        application_section: selectedAppSection
      });
      if (updateMilestoneRes?.data) {
        setEditEndDateAdjusted(false);
        setEndDateAdjusted(updatedEndDateAdjusted.end_date_adjusted);
        setUpdated(true);
        setIsUpdate(false);
        await getData();
        dispatchApp(setMessage({ msgDetails: 'Milestone updated successfully', severity: 'success' }));
      } else if (updateMilestoneRes?.error) {
        if (updateMilestoneRes?.error.status === 403) {
          dispatchApp(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
        } else if (updateMilestoneRes?.error.status === 401) {
          dispatchApp(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
        } else {
          dispatchApp(setMessage({ msgDetails: mileStoneErrorMsg, severity: 'error' }));
        }
      }
    } else {
      setEditEndDateAdjusted(false);
    }
    setLoadingEndDA(false);
  };
  /**End*/

  const handleStartDatePlanned = async () => {
    if (startDatePlanned === null || startDatePlanned === '') {
      dispatchApp(setMessage({ msgDetails: 'Please enter Start Date Planned Baseline', severity: 'error' }));
    } else if (updatedStartDatePlanned.start_date_planned_baseline === 'Invalid date') {
      dispatchApp(setMessage({ msgDetails: 'Invalid Date', severity: 'error' }));
    } else if (startDatePlanned > endDatePlanned) {
      dispatchApp(
        setMessage({
          msgDetails: 'Start Date Planned Baseline can not be greater than End Date Planned Baseline',
          severity: 'error'
        })
      );
    } else if (selectedMilestone.start_date_planned_baseline !== updatedStartDatePlanned.start_date_planned_baseline) {
      setLoadingStartDB(true);
      const updatedStartDateRes: any = await updateMilestoneStartPlannedDates({
        study_id: studyId,
        country: selectedCountry,
        site_id: selectedSite,
        application: process.env.REACT_APP_NAME,
        milestone_id: selectedMilestone.milestone_id,
        data: updatedStartDatePlanned,
        application_section: selectedAppSection
      });
      if (updatedStartDateRes?.data) {
        setEditStartDatePlanned(false);
        setStartDatePlanned(updatedStartDatePlanned.start_date_planned_baseline);
        setUpdated(true);
        setIsUpdate(false);
      } else if (updatedStartDateRes?.error) {
        if (updatedStartDateRes?.error.status === 403) {
          dispatchApp(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
        } else if (updatedStartDateRes?.error.status === 401) {
          dispatchApp(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
        } else {
          dispatchApp(setMessage({ msgDetails: mileStoneErrorMsg, severity: 'error' }));
        }
      }
    } else {
      setEditStartDatePlanned(false);
    }
    setLoadingStartDB(false);
  };

  const handleEndDatePlanned = async () => {
    if (
      (startDatePlanned === null || startDatePlanned === '') &&
      selectedMilestone.end_date_planned_baseline !== updatedEndDatePlanned.end_date_planned_baseline
    ) {
      dispatchApp(setMessage({ msgDetails: 'Please enter Start Date Planned Baseline', severity: 'error' }));
    } else if (updatedEndDatePlanned.end_date_planned_baseline === 'Invalid date') {
      dispatchApp(setMessage({ msgDetails: 'Invalid Date', severity: 'error' }));
    } else if (endDatePlanned < startDatePlanned) {
      dispatchApp(
        setMessage({
          msgDetails: 'End Date Planned Baseline can not be lesser than Start Date Planned Baseline',
          severity: 'error'
        })
      );
    } else if (selectedMilestone.end_date_planned_baseline !== updatedEndDatePlanned.end_date_planned_baseline) {
      setLoadingEndDB(true);
      const updatedEndDateRes: any = await updateMilestoneEndPlannedDates({
        study_id: studyId,
        country: selectedCountry,
        site_id: selectedSite,
        application: process.env.REACT_APP_NAME,
        milestone_id: selectedMilestone.milestone_id,
        data: updatedEndDatePlanned,
        application_section: selectedAppSection
      });
      if (updatedEndDateRes?.data) {
        setEditEndDatePlanned(false);
        setEndDatePlanned(updatedEndDatePlanned.end_date_planned_baseline);
        setUpdated(true);
        setIsUpdate(false);
      } else if (updatedEndDateRes?.error) {
        if (updatedEndDateRes?.error.status === 403) {
          dispatchApp(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
        } else if (updatedEndDateRes?.error.status === 401) {
          dispatchApp(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
        } else {
          dispatchApp(setMessage({ msgDetails: mileStoneErrorMsg, severity: 'error' }));
        }
      }
    } else {
      setEditEndDatePlanned(false);
    }
    setLoadingEndDB(false);
  };

  const handleAssignedToUpdate = async () => {
    const assignedValue: string = (document.getElementById('assignedToField') as HTMLInputElement).value;
    if (selectedMilestone.assigned_to !== assignedValue) {
      setAssignedTo(assignedValue);
      setupdatedAssignedTo({ ...updatedAssignedTo, assigned_to: assignedValue });
      setLoadingAssignedTo(true);
      const assigneeUpdateRes: any = await updateMilestone({
        study_id: studyId,
        country: selectedCountry,
        site_id: selectedSite,
        application: process.env.REACT_APP_NAME,
        milestone_id: selectedMilestone.milestone_id,
        data: { updated_by: user, assigned_to: assignedToRef.current.value },
        application_section: selectedAppSection
      });
      if (assigneeUpdateRes?.data) {
        setEditAssignedTo(false);
        setUpdated(true);
        setIsUpdate(false);
        await getData();
        dispatchApp(setMessage({ msgDetails: mileStoneDataUpdatedMsg, severity: 'success' }));
      } else if (assigneeUpdateRes?.error) {
        if (assigneeUpdateRes?.error.status === 403) {
          dispatchApp(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
        } else if (assigneeUpdateRes?.error.status === 401) {
          dispatchApp(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
        } else {
          dispatchApp(setMessage({ msgDetails: mileStoneErrorMsg, severity: 'error' }));
        }
      }
    } else {
      setEditAssignedTo(false);
    }
    setLoadingAssignedTo(false);
  };

  const handleModalClose = () => {
    const commentInitValues = selectedMilestone.milestone_comments !== null ? selectedMilestone.milestone_comments : '';
    if (isUpdate || (commentRef?.current?.value && commentRef?.current?.value !== commentInitValues)) {
      setNextTabValue(0);
      setOpenConfirmExit(true);
    } else {
      handleClose();
    }
  };

  const handlePrint = () => {
    setPaginatedAuditDetails(groupBy(auditTrailData));
    setTimeout(() => {
      window.print();
    }, 300);
    setTimeout(() => {
      setPaginationWithGroupBy(auditTrailData, page);
    }, 300);
  };

  const handleAssignedToChange = (e: any) => {
    setIsUpdate(true);
  };

  const [display, setDisplay] = useState('inline');

  const handleFormatDate = (dateString: any) => {
    return preferedDateFormat(dateString?.split('T')[0]);
  };

  const handleTimeAmPm = (time: any) => {
    const timeString = time.slice(11, 19);
    const timeString12hr = new Date(`1970-01-01T${timeString}Z`).toLocaleTimeString('en-US', {
      timeZone: 'UTC',
      hour12: true,
      hour: 'numeric',
      minute: 'numeric'
    });
    return timeString12hr;
  };
  // checking details tab edited
  const checkDetailsTabInEditMode = () => {
    if (
      (editStartDatePlanned ||
        editEndDatePlanned ||
        editStartDateActual ||
        editEndDateActual ||
        editStartDateAdjusted ||
        editEndDateAdjusted ||
        editAssignedTo) &&
      isUpdate
    ) {
      setNextTabValue(1);
      setOpenConfirmExit(true);
    } else {
      if (updated) {
        setUpdated(false);
        setValue(1);
      } else {
        setValue(1);
      }
      setUpdatableFieldsToFalse();
    }
  };
  const handleMilestonesDetailsNextBtn = async (tabVal: number) => {
    if (tabVal === 0) {
      const commentInitValues =
        selectedMilestone.milestone_comments !== null ? selectedMilestone.milestone_comments : '';
      if (commentRef?.current?.value && commentRef?.current?.value !== commentInitValues) {
        const commentObj: any = { milestone_comments: commentRef.current.value, updated_by: user };
        const updateMilestonRes: any = await updateMilestone({
          study_id: studyId,
          country: selectedCountry,
          site_id: selectedSite,
          application: process.env.REACT_APP_NAME,
          milestone_id: selectedMilestone.milestone_id,
          data: commentObj,
          application_section: selectedAppSection
        });
        checkDetailsTabInEditMode();
        await getData();
        if (updateMilestonRes?.error) {
          dispatchApp(setMessage({ msgDetails: mileStoneErrorMsg, severity: 'error' }));
        }
      } else {
        checkDetailsTabInEditMode();
      }
    } else {
      if (tabVal === 1) {
        setValue(2);
      }
      if (tabVal === 2) {
        getAuditHistory(
          selectedMilestone.milestone_id,
          0,
          maxSizeOfAuditDetails,
          sortBy == 'Newest' ? 'desc' : 'asc',
          audiFilterDataObj
        );
        setValue(3);
      }
      if (tabVal === 3) {
        handleClose();
      }
      setUpdatableFieldsToFalse();
    }
  };
  const priorityClassName =
    (selectedMilestone.priority === 'High' && 'high') ||
    (selectedMilestone.priority === 'Medium' && 'medium') ||
    (selectedMilestone.priority === 'Low' && 'low');
  const priorityLabel =
    (selectedMilestone.priority === 'High' && 'High') ||
    (selectedMilestone.priority === 'Medium' && 'Medium') ||
    (selectedMilestone.priority === 'Low' && 'Low');
  React.useEffect(() => {
    if (selectedMilestone.milestone_status === 'Completed') {
      setDisplay('none');
    } else {
      setDisplay('inline-block');
    }
  }, [milestoneStatus]);

  const [filterText, setFilterText] = useState('');
  const handleSearch = (event: any) => {
    const searchValue: string = event.target.value;
    setFilterText(searchValue);
    setFilteredAuditList(searchValue);
  };

  const setFilteredAuditList = (searchValue: string) => {
    if (searchValue != '') {
      filterAuditList(searchValue);
    } else {
      setNoOfAuditResults(auditTrailData.length);
      setPaginationWithGroupBy(auditTrailData, 1);
      setPage(1);
    }
  };

  const filterAuditList = (value: string) => {
    let filtered = auditTrailData.filter((data: any) => {
      if (
        data.reason_data_changed?.toLowerCase().includes(value.toLowerCase()) ||
        data.field_changed?.toLowerCase().includes(value.toLowerCase()) ||
        data.field_label?.toLowerCase().includes(value.toLowerCase()) ||
        data.changed_data?.toLowerCase().includes(value.toLowerCase()) ||
        data.audit_user_name?.toLowerCase().includes(value.toLowerCase()) ||
        data.data_changed_from?.toLowerCase().includes(value.toLowerCase())
      ) {
        return data;
      }
    });
    if (sortBy === 'Newest') {
      filtered = filtered.sort((a: any, b: any) => +new Date(b.date_time_of_change) - +new Date(a.date_time_of_change));
    } else {
      filtered = filtered.sort((a: any, b: any) => +new Date(a.date_time_of_change) - +new Date(b.date_time_of_change));
    }
    setNoOfAuditResults(filtered.length);
    setPaginationWithGroupBy(filtered, 1);
    setPage(1);
  };

  const [expandAll, setExpandAll] = React.useState(false);

  // Newly added Search Filter
  const [openFilterSearch, setOpenFilterSearch] = React.useState(false);

  const handleClickOpenFilterSearch = () => {
    setOpenFilterSearch(true);
    setUpdatedFilterObject(appliedFilterObject);
  };

  const handleCloseFilterSearch = () => {
    setOpenFilterSearch(false);
    setUpdatedFilterObject(appliedFilterObject);
  };

  const handleChange2 = (e: any) => {
    const val = e.target.value;
    const formattedVal = typeof val === 'string' ? formatInput(val) : val;
    setComments(formattedVal);
  };

  const headers = [
    { label: 'Date of Creation', key: 'created_on' },
    { label: 'Date of Change', key: 'date_time_of_change' },
    { label: 'Project Module', key: 'audit_project_module' },
    { label: 'Module Section', key: 'audit_module_section' },
    { label: 'User Name', key: 'audit_user_name' },
    { label: 'Field Name', key: 'field_changed' },
    { label: 'Field Data', key: 'changed_data' },
    { label: 'Previous Data', key: 'data_changed_from' },
    { label: 'Reason For Change', key: 'comments' },
    { label: 'Action Type', key: 'reason_data_changed' }
  ];
  const [appliedFilterCount, setAppliedFilterCount] = React.useState<number>(0);
  const [openConfirmExit, setOpenConfirmExit] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [nextTabValue, setNextTabValue] = useState(value);

  const [csvDataDownload, setCsvDataDownload] = useState([]);

  useEffect(() => {
    const csvData = auditTrailData.map((item: any) => {
      return {
        ...item,
        date_time_of_change: moment(new Date(item.date_time_of_change))
          .format(`${userPreferedDateFormat.toUpperCase()} hh:mm:ss A`)
          .toString(),
        created_on: moment(new Date(item.created_on))
          .format(`${userPreferedDateFormat.toUpperCase()} hh:mm:ss A`)
          .toString()
      };
    });
    setCsvDataDownload(csvData);
  }, [auditTrailData]);

  const [collapseItems, setCollapseItems] = useState<ICollapseItem>({});
  const handleClickExpandAll = () => {
    const expandState = getText() === expandAllTxt ? true : false;
    setCollapseItems({} as ICollapseItem);
    setExpandAll(expandState);
  };
  const handleChangeAccordions = (_: React.SyntheticEvent<Element, Event>, expanded: boolean, id: string) => {
    const newCollapse: ICollapseItem = { ...collapseItems };
    newCollapse[id] = expanded;
    setCollapseItems(newCollapse);
  };

  const getExpandAndCollapseState = (id: string): boolean => {
    let result = expandAll;
    if (collapseItems[id] !== undefined) {
      result = collapseItems[id];
    }
    return result;
  };

  const disableNABtn =
    selectedMilestone.is_activated &&
    (selectedMilestone.milestone_status === 'Not set' || selectedMilestone.milestone_status === 'Planned');
  const disableHoldBtn = selectedMilestone.is_activated && selectedMilestone.milestone_status === 'In progress';
  const getUpdatedStatus = (status: string) => {
    const updateObj = {
      milestone_ids: [selectedMilestone.milestone_id],
      milestone_status: status,
      is_activated: false,
      updated_by: user
    };

    updateMilestoneDataStatus({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: process.env.REACT_APP_NAME,
      data: updateObj,
      application_section: selectedAppSection
    });
  };

  useEffect(() => {
    if (updateMilestoneSuccess || reactivateMilestoneSucess) {
      dispatch(setMessage({ msgDetails: 'Milestone updated successfully', severity: 'success' }));
      getData();
    }
  }, [updateMilestoneSuccess, reactivateMilestoneSucess]);

  useEffect(() => {
    if (updateStartPlanneddateSuccess || updateEndPlanneddateSuccess) {
      dispatchApp(setMessage({ msgDetails: mileStoneDataUpdatedMsg, severity: 'success' }));
      getData();
    }
  }, [updateStartPlanneddateSuccess, updateEndPlanneddateSuccess]);

  const getText = () => {
    let txt = expandAllTxt;
    const expandedItems = Object.entries(collapseItems).filter(item => item[1]);
    const collapsedItems = Object.entries(collapseItems).filter(item => !item[1]);
    txt = expandAll ? collapseAllTxt : expandAllTxt;
    if (Object.entries(collapseItems).length > 0 && (collapsedItems.length > 0 || expandedItems.length > 0)) {
      if (paginatedAuditDetails.length === collapsedItems.length) {
        txt = expandAllTxt;
      } else if (paginatedAuditDetails.length === expandedItems.length) {
        txt = collapseAllTxt;
      } else {
        txt = expandAllTxt;
      }
    }
    return txt;
  };

  // handling confim exit popup selecting ok
  const handleConfirmExitOK = () => {
    setUpdatableFieldsToFalse();
    setValue(nextTabValue);
    setOpenConfirmExit(false);
    setIsUpdate(false);
    if (nextTabValue === 0) {
      handleClose();
    }
  };
  // setting updatable fields to false and to original values
  const setUpdatableFieldsToFalse = () => {
    if (editStartDatePlanned) {
      setEditStartDatePlanned(false);
      if (isUpdate) {
        setStartDatePlanned(selectedMilestone.start_date_planned_baseline);
      }
    }
    if (editEndDatePlanned) {
      setEditEndDatePlanned(false);
      if (isUpdate) {
        setEndDatePlanned(selectedMilestone.endDatePlanned);
      }
    }
    if (editStartDateAdjusted) {
      setEditStartDateAdjusted(false);
      if (isUpdate) {
        setStartDateAdjusted(selectedMilestone.start_date_adjusted);
      }
    }
    if (editEndDateAdjusted) {
      setEditEndDateAdjusted(false);
      if (isUpdate) {
        setEndDateAdjusted(selectedMilestone.endDateAdjusted);
      }
    }
    if (editStartDateActual) {
      setEditStartDateActual(false);
      if (isUpdate) {
        setStartDateActual(selectedMilestone.start_date_actual);
      }
    }
    if (editEndDateActual) {
      setEditEndDateActual(false);
      if (isUpdate) {
        setEndDateActual(selectedMilestone.end_date_actual);
      }
    }
    if (editAssignedTo) {
      setEditAssignedTo(false);
      if (isUpdate) {
        setAssignedTo(selectedMilestone.assigned_to);
      }
    }
  };

  useEffect(() => {
    isGridDataLoading && openDetailsModal ? dispatch(showLoader()) : dispatch(hideLoader());
  }, [isGridDataLoading, openDetailsModal]);

  return (
    <>
      <DialogTitle id="customized-dialog-title">
        {`${selectedMilestone.major_milestone} - ${selectedMilestone.minor_milestone}`}
        <Button className={`error-btn ${priorityClassName}`} size="small" sx={{ pointerEvents: 'none' }}>
          <ErrorIcon />
          {priorityLabel}
        </Button>
      </DialogTitle>
      <DialogContent>
        <div className="query-modal" style={{ padding: 0 }}>
          <div className="card">
            <Loader />
            <Grid container spacing={3}>
              <Grid md={12}>
                <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex' }}>
                  <Grid container>
                    <Grid md={2.3} id="no-print">
                      <Tabs
                        orientation="vertical"
                        variant="scrollable"
                        value={value}
                        onChange={handleTabsChange}
                        aria-label="Vertical tabs example"
                        sx={{ borderRight: 1, borderColor: 'divider', position: 'sticky', top: '0px' }}
                      >
                        <Tab label="Details" {...a11yProps(0)} />
                        <Tab label="Progress" {...a11yProps(1)} />
                        <Tab label="Associated Documents" {...a11yProps(2)} />
                        <Tab label="Audit trail" {...a11yProps(3)} />
                      </Tabs>
                    </Grid>
                    <Grid md={9.7} sx={{ '@media print': { width: '100%' } }}>
                      <TabPanel value={value} index={0} className="details-tab-content">
                        <Box sx={{ mt: -1 }}>
                          <Grid container spacing={2}>
                            <Grid item md={4}>
                              <strong>Major Milestone:</strong>{' '}
                              <span className="min18H">{selectedMilestone.major_milestone}</span>
                            </Grid>
                            <Grid item md={4}>
                              {!editStartDatePlanned && !loadingStartDB ? (
                                <div style={{ position: 'relative' }}>
                                  <strong>Start Date Planned Baseline:</strong>{' '}
                                  <span className="min18H">{handleFormatDate(startDatePlanned)}</span>
                                  {plannedDateConditions ? (
                                    <IconButton
                                      style={{ position: 'absolute', right: '8px', top: '4px', display }}
                                      href="#contained-buttons"
                                      className="grey-btn btn-static"
                                      disabled={
                                        editEndDateAdjusted ||
                                        editStartDateAdjusted ||
                                        editEndDatePlanned ||
                                        editAssignedTo ||
                                        editStartDateActual ||
                                        editEndDateActual
                                      }
                                      onClick={() => setEditStartDatePlanned(true)}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  ) : (
                                    ''
                                  )}{' '}
                                </div>
                              ) : (
                                <Stack spacing={2} direction="row" alignItems="flex-end">
                                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                      maxDate={
                                        selectedMilestone.end_date_planned_baseline !== null
                                          ? selectedMilestone.end_date_planned_baseline
                                          : null
                                      }
                                      label="Start Date Planned Baseline"
                                      inputFormat={userPreferedDateFormat.toUpperCase()}
                                      value={preferedFormatForDatePicker(startDatePlanned)}
                                      onChange={(newValue: any) => {
                                        if (newValue === null) {
                                          setUpdatedStartDatePlanned({
                                            ...updatedStartDatePlanned,
                                            start_date_planned_baseline: null
                                          });
                                          setStartDatePlanned(null);
                                        } else {
                                          const changedDate = moment(newValue.$d).format(SERVER_DATE_FORMAT);
                                          setStartDatePlanned(changedDate);
                                          setUpdatedStartDatePlanned({
                                            ...updatedStartDatePlanned,
                                            start_date_planned_baseline: changedDate.length < 10 ? null : changedDate
                                          });
                                        }

                                        setIsUpdate(true);
                                      }}
                                      renderInput={(params: any) => {
                                        if (
                                          updatedEndDatePlanned.start_date_planned_baseline === 'Invalid date' &&
                                          params.inputProps.value === ''
                                        ) {
                                          setUpdatedStartDatePlanned({
                                            ...updatedStartDatePlanned,
                                            start_date_planned_baseline: null
                                          });
                                          setStartDatePlanned(null);
                                        }
                                        return (
                                          <TextField
                                            {...params}
                                            error={
                                              updatedEndDatePlanned.start_date_planned_baseline === 'Invalid date' &&
                                              params.inputProps.value !== ''
                                            }
                                            variant="standard"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        );
                                      }}
                                    />
                                  </LocalizationProvider>
                                  {loadingStartDB ? (
                                    <LoadingButton loading variant="text">
                                      &nbsp;
                                    </LoadingButton>
                                  ) : (
                                    <Button href="#contained-buttons" onClick={handleStartDatePlanned}>
                                      Save
                                    </Button>
                                  )}
                                </Stack>
                              )}
                            </Grid>
                            <Grid item md={4}>
                              {' '}
                              {!editStartDateAdjusted && !loadingStartDA ? (
                                <div style={{ position: 'relative' }}>
                                  <strong>Start Date Adjusted:</strong>{' '}
                                  <span className="min18H">{handleFormatDate(startDateAdjusted)}</span>
                                  {selectedMilestone.is_update_eligible &&
                                  selectedMilestone.is_activated &&
                                  selectedMilestone.start_date_planned_baseline !== null &&
                                  selectedMilestone.end_date_planned_baseline !== null &&
                                  selectedMilestone.start_date_actual == null ? (
                                    <Button
                                      style={{ position: 'absolute', right: '8px', top: '4px', display }}
                                      href="#contained-buttons"
                                      className="grey-btn btn-static"
                                      disabled={
                                        editEndDateAdjusted ||
                                        editStartDatePlanned ||
                                        editEndDatePlanned ||
                                        editAssignedTo ||
                                        editEndDateActual ||
                                        editStartDateActual
                                      }
                                      onClick={() => setEditStartDateAdjusted(true)}
                                    >
                                      <EditIcon />
                                    </Button>
                                  ) : (
                                    ''
                                  )}{' '}
                                </div>
                              ) : (
                                <Stack spacing={2} direction="row" alignItems="flex-end">
                                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                      maxDate={updatedEndDateAdjusted.end_date_adjusted}
                                      label="Start Date Adjusted"
                                      inputFormat={userPreferedDateFormat.toUpperCase()}
                                      value={preferedFormatForDatePicker(startDateAdjusted)}
                                      onChange={(newValue: any) => {
                                        if (newValue === null) {
                                          setUpdatedStartDateAdjusted({
                                            ...updatedStartDateAdjusted,
                                            start_date_adjusted: null
                                          });
                                          setStartDateAdjusted(null);
                                        } else {
                                          const changedDate = moment(newValue.$d).format(SERVER_DATE_FORMAT);
                                          setStartDateAdjusted(changedDate);
                                          setUpdatedStartDateAdjusted({
                                            ...updatedStartDateAdjusted,
                                            start_date_adjusted: changedDate.length < 10 ? null : changedDate
                                          });
                                        }

                                        setIsUpdate(true);
                                      }}
                                      renderInput={(params: any) => {
                                        if (
                                          updatedEndDatePlanned.start_date_adjusted === 'Invalid date' &&
                                          params.inputProps.value === ''
                                        ) {
                                          setUpdatedStartDateAdjusted({
                                            ...updatedStartDateAdjusted,
                                            start_date_adjusted: null
                                          });
                                          setStartDateAdjusted(null);
                                        }
                                        return (
                                          <TextField
                                            {...params}
                                            error={
                                              updatedEndDatePlanned.start_date_adjusted === 'Invalid date' &&
                                              params.inputProps.value !== ''
                                            }
                                            variant="standard"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        );
                                      }}
                                    />
                                  </LocalizationProvider>
                                  {loadingStartDA ? (
                                    <LoadingButton loading variant="text">
                                      &nbsp;
                                    </LoadingButton>
                                  ) : (
                                    <Button href="#contained-buttons" onClick={handleStartDateAdjusted}>
                                      Save
                                    </Button>
                                  )}
                                </Stack>
                              )}
                            </Grid>
                          </Grid>
                          <Grid container spacing={2} rowSpacing={4}>
                            <Grid item md={4}>
                              <strong>Minor Milestone:</strong>{' '}
                              <span className="min18H">{selectedMilestone.minor_milestone}</span>
                            </Grid>
                            <Grid item md={4}>
                              {!editStartDateActual && !loadingStartDateActual ? (
                                <div style={{ position: 'relative' }}>
                                  <strong>Start Date Actual:</strong>{' '}
                                  <span className="min18H">{handleFormatDate(startDateActual)}</span>
                                  {selectedMilestone.is_update_eligible &&
                                  selectedMilestone.is_activated &&
                                  selectedMilestone.start_date_planned_baseline !== null &&
                                  selectedMilestone.end_date_planned_baseline !== null &&
                                  checkPermission('update_milestones_actual_dates') ? (
                                    <Button
                                      style={{ position: 'absolute', right: '8px', top: '4px', display }}
                                      href="#contained-buttons"
                                      className="grey-btn btn-static"
                                      disabled={
                                        editStartDatePlanned ||
                                        editEndDatePlanned ||
                                        editAssignedTo ||
                                        editStartDateAdjusted ||
                                        editEndDateAdjusted ||
                                        editEndDateActual
                                      }
                                      onClick={() => setEditStartDateActual(true)}
                                    >
                                      <EditIcon />
                                    </Button>
                                  ) : null}{' '}
                                </div>
                              ) : (
                                <Stack spacing={2} direction="row" alignItems="flex-end">
                                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                      maxDate={dayjs(new Date())}
                                      label="Start Date Actual"
                                      inputFormat={userPreferedDateFormat.toUpperCase()}
                                      value={preferedFormatForDatePicker(startDateActual)}
                                      onChange={(newValue: any) => {
                                        if (newValue === null) {
                                          setUpdatedStartDateActual({
                                            ...updatedStartDateActual,
                                            start_date_actual: null
                                          });
                                          setStartDateActual(null);
                                        } else {
                                          const changedDate = moment(newValue.$d).format(SERVER_DATE_FORMAT);
                                          setStartDateActual(changedDate);
                                          setUpdatedStartDateActual({
                                            ...updatedStartDateActual,
                                            start_date_actual: changedDate.length < 10 ? null : changedDate
                                          });
                                        }

                                        setIsUpdate(true);
                                      }}
                                      renderInput={(params: any) => {
                                        if (
                                          updatedEndDatePlanned.start_date_actual === 'Invalid date' &&
                                          params.inputProps.value === ''
                                        ) {
                                          setUpdatedStartDateActual({
                                            ...updatedStartDateActual,
                                            start_date_actual: null
                                          });
                                          setStartDateActual(null);
                                        }
                                        return (
                                          <TextField
                                            {...params}
                                            error={
                                              updatedEndDatePlanned.start_date_actual === 'Invalid date' &&
                                              params.inputProps.value !== ''
                                            }
                                            variant="standard"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        );
                                      }}
                                    />
                                  </LocalizationProvider>
                                  {loadingStartDateActual ? (
                                    <LoadingButton loading variant="text">
                                      &nbsp;
                                    </LoadingButton>
                                  ) : (
                                    <Button href="#contained-buttons" onClick={handleStartDateActual}>
                                      Save
                                    </Button>
                                  )}
                                </Stack>
                              )}
                            </Grid>

                            <Grid item md={4}>
                              {!editEndDateAdjusted && !loadingEndDA ? (
                                <div style={{ position: 'relative' }}>
                                  <strong>End Date Adjusted:</strong>{' '}
                                  <span className="min18H">{handleFormatDate(endDateAdjusted)}</span>
                                  {selectedMilestone.is_update_eligible &&
                                  selectedMilestone.is_activated &&
                                  selectedMilestone.start_date_planned_baseline !== null &&
                                  selectedMilestone.end_date_planned_baseline !== null &&
                                  selectedMilestone.end_date_actual == null ? (
                                    <Button
                                      style={{ position: 'absolute', right: '8px', top: '4px', display }}
                                      href="#contained-buttons"
                                      className="grey-btn btn-static"
                                      disabled={
                                        editStartDateAdjusted ||
                                        editStartDatePlanned ||
                                        editEndDatePlanned ||
                                        editAssignedTo ||
                                        editStartDateActual ||
                                        editEndDateActual
                                      }
                                      onClick={() => setEditEndDateAdjusted(true)}
                                    >
                                      <EditIcon />
                                    </Button>
                                  ) : (
                                    ''
                                  )}{' '}
                                </div>
                              ) : (
                                <Stack spacing={2} direction="row" alignItems="flex-end">
                                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                      minDate={preferedFormatForDatePicker(
                                        updatedStartDateAdjusted.start_date_adjusted
                                      )}
                                      label="End Date Adjusted"
                                      inputFormat={userPreferedDateFormat.toUpperCase()}
                                      value={preferedFormatForDatePicker(endDateAdjusted)}
                                      onChange={(newValue: any) => {
                                        if (newValue === null) {
                                          setUpdatedEndDateAdjusted({
                                            ...updatedEndDateAdjusted,
                                            end_date_adjusted: null
                                          });
                                          setEndDateAdjusted(null);
                                        } else {
                                          const changedDate = moment(newValue.$d).format(SERVER_DATE_FORMAT);
                                          setEndDateAdjusted(changedDate);
                                          setUpdatedEndDateAdjusted({
                                            ...updatedEndDateAdjusted,
                                            end_date_adjusted: changedDate.length < 10 ? null : changedDate
                                          });
                                        }
                                        setIsUpdate(true);
                                      }}
                                      renderInput={(params: any) => {
                                        if (
                                          updatedEndDatePlanned.end_date_adjusted === 'Invalid date' &&
                                          params.inputProps.value === ''
                                        ) {
                                          setUpdatedEndDateAdjusted({
                                            ...updatedEndDateAdjusted,
                                            end_date_adjusted: null
                                          });
                                          setEndDateAdjusted(null);
                                        }
                                        return (
                                          <TextField
                                            {...params}
                                            error={
                                              updatedEndDatePlanned.end_date_adjusted === 'Invalid date' &&
                                              params.inputProps.value !== ''
                                            }
                                            variant="standard"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        );
                                      }}
                                    />
                                  </LocalizationProvider>
                                  {loadingEndDA ? (
                                    <LoadingButton loading variant="text">
                                      &nbsp;
                                    </LoadingButton>
                                  ) : (
                                    <Button href="#contained-buttons" onClick={handleEndDateAdjusted}>
                                      Save
                                    </Button>
                                  )}
                                </Stack>
                              )}
                            </Grid>
                          </Grid>
                          <Grid container spacing={2} rowSpacing={4}>
                            <Grid item md={4}>
                              <strong>Study Stage:</strong>{' '}
                              <span className="min18H">{selectedMilestone.study_stage}</span>
                            </Grid>
                            <Grid item md={4}>
                              {!editEndDatePlanned && !loadingEndDB ? (
                                <div style={{ position: 'relative' }}>
                                  <strong>End Date Planned Baseline:</strong>{' '}
                                  <span className="min18H">{handleFormatDate(endDatePlanned)}</span>
                                  {plannedDateConditions ? (
                                    <Button
                                      style={{ position: 'absolute', right: '8px', top: '4px', display }}
                                      href="#contained-buttons"
                                      className="grey-btn btn-static"
                                      disabled={
                                        editStartDatePlanned ||
                                        editEndDateAdjusted ||
                                        editStartDateAdjusted ||
                                        editAssignedTo ||
                                        editStartDateActual ||
                                        editEndDateActual
                                      }
                                      onClick={() => setEditEndDatePlanned(true)}
                                    >
                                      <EditIcon />
                                    </Button>
                                  ) : (
                                    ''
                                  )}{' '}
                                </div>
                              ) : (
                                <Stack spacing={2} direction="row" alignItems="flex-end">
                                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                      minDate={preferedFormatForDatePicker(
                                        updatedStartDatePlanned.start_date_planned_baseline
                                      )}
                                      label="End Date Planned Baseline"
                                      inputFormat={userPreferedDateFormat.toUpperCase()}
                                      value={preferedFormatForDatePicker(endDatePlanned)}
                                      onChange={(newValue: any) => {
                                        if (newValue === null) {
                                          setUpdatedEndDatePlanned({
                                            ...updatedEndDatePlanned,
                                            end_date_planned_baseline: null
                                          });
                                          setEndDatePlanned(null);
                                        } else {
                                          const changedDate = moment(newValue.$d).format(SERVER_DATE_FORMAT);
                                          setEndDatePlanned(changedDate);
                                          setUpdatedEndDatePlanned({
                                            ...updatedEndDatePlanned,
                                            end_date_planned_baseline: changedDate.length < 10 ? null : changedDate
                                          });
                                        }
                                        setIsUpdate(true);
                                      }}
                                      renderInput={(params: any) => {
                                        if (
                                          updatedEndDatePlanned.end_date_planned_baseline === 'Invalid date' &&
                                          params.inputProps.value === ''
                                        ) {
                                          setUpdatedEndDatePlanned({
                                            ...updatedEndDatePlanned,
                                            end_date_planned_baseline: null
                                          });
                                          setEndDatePlanned(null);
                                        }
                                        return (
                                          <TextField
                                            {...params}
                                            error={
                                              updatedEndDatePlanned.end_date_planned_baseline === 'Invalid date' &&
                                              params.inputProps.value !== ''
                                            }
                                            variant="standard"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        );
                                      }}
                                    />
                                  </LocalizationProvider>
                                  {loadingEndDB ? (
                                    <LoadingButton loading variant="text">
                                      &nbsp;
                                    </LoadingButton>
                                  ) : (
                                    <Button href="#contained-buttons" onClick={handleEndDatePlanned}>
                                      Save
                                    </Button>
                                  )}
                                </Stack>
                              )}
                            </Grid>
                            <Grid item md={4}>
                              <div style={{ position: 'relative' }}>
                                <strong>Milestone Status</strong> <span className="min18H">{milestoneStatus}</span>
                              </div>
                            </Grid>
                          </Grid>
                          <Grid container spacing={2} rowSpacing={4}>
                            <Grid item md={4}>
                              <strong>Updated By:</strong>{' '}
                              <span className="min18H">{selectedMilestone.updated_by}</span>
                            </Grid>

                            <Grid item md={4}>
                              {!editEndDateActual && !loadingEndDateActual ? (
                                <div style={{ position: 'relative' }}>
                                  <strong>End Date Actual:</strong>{' '}
                                  <span className="min18H">{handleFormatDate(endDateActual)}</span>
                                  {selectedMilestone.is_update_eligible &&
                                  selectedMilestone.is_activated &&
                                  selectedMilestone.start_date_planned_baseline !== null &&
                                  selectedMilestone.end_date_planned_baseline !== null &&
                                  checkPermission('update_milestones_actual_dates') ? (
                                    <Button
                                      style={{ position: 'absolute', right: '8px', top: '4px', display }}
                                      href="#contained-buttons"
                                      className="grey-btn btn-static"
                                      disabled={
                                        startDateActual === null ||
                                        editStartDateActual ||
                                        editStartDatePlanned ||
                                        editEndDatePlanned ||
                                        editAssignedTo ||
                                        editStartDateActual
                                      }
                                      onClick={() => setEditEndDateActual(true)}
                                    >
                                      <EditIcon />
                                    </Button>
                                  ) : null}{' '}
                                </div>
                              ) : (
                                <Stack spacing={2} direction="row" alignItems="flex-end">
                                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                      maxDate={dayjs(new Date())}
                                      label="End Date Actual"
                                      inputFormat={userPreferedDateFormat.toUpperCase()}
                                      value={preferedFormatForDatePicker(endDateActual)}
                                      onChange={(newValue: any) => {
                                        if (newValue === null) {
                                          setUpdatedEndDateActual({
                                            ...updatedEndDateActual,
                                            end_date_actual: null
                                          });
                                          setEndDateActual(null);
                                        } else {
                                          const changedDate = moment(newValue.$d).format(SERVER_DATE_FORMAT);
                                          setEndDateActual(changedDate);
                                          setUpdatedEndDateActual({
                                            ...updatedEndDateActual,
                                            end_date_actual: changedDate.length < 10 ? null : changedDate
                                          });
                                        }

                                        setIsUpdate(true);
                                      }}
                                      renderInput={(params: any) => {
                                        if (
                                          updatedEndDatePlanned.end_date_actual === 'Invalid date' &&
                                          params.inputProps.value === ''
                                        ) {
                                          setUpdatedEndDateActual({
                                            ...updatedEndDateActual,
                                            end_date_actual: null
                                          });
                                          setEndDateActual(null);
                                        }
                                        return (
                                          <TextField
                                            {...params}
                                            error={
                                              updatedEndDatePlanned.end_date_actual === 'Invalid date' &&
                                              params.inputProps.value !== ''
                                            }
                                            variant="standard"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        );
                                      }}
                                    />
                                  </LocalizationProvider>
                                  {loadingEndDateActual ? (
                                    <LoadingButton loading variant="text">
                                      &nbsp;
                                    </LoadingButton>
                                  ) : (
                                    <Button href="#contained-buttons" onClick={handleEndDateActual}>
                                      Save
                                    </Button>
                                  )}
                                </Stack>
                              )}
                            </Grid>

                            <Grid item md={4}>
                              {' '}
                              {!editAssignedTo && !loadingAssignedTo ? (
                                <div style={{ position: 'relative' }}>
                                  <strong>Assigned To:</strong> <span className="min18H">{assignedTo}</span>
                                  {selectedMilestone.is_update_eligible ? (
                                    <Button
                                      style={{ position: 'absolute', right: '8px', top: '4px', display }}
                                      href="#contained-buttons"
                                      className="grey-btn btn-static"
                                      disabled={
                                        editEndDateAdjusted ||
                                        editStartDatePlanned ||
                                        editEndDatePlanned ||
                                        editStartDateAdjusted ||
                                        editStartDateActual ||
                                        editEndDateActual
                                      }
                                      onClick={() => setEditAssignedTo(true)}
                                    >
                                      <EditIcon />
                                    </Button>
                                  ) : (
                                    ''
                                  )}{' '}
                                </div>
                              ) : (
                                <Stack spacing={2} direction="row" alignItems="flex-end">
                                  <TextField
                                    id="assignedToField"
                                    label="Assigned To:"
                                    name="assigned_to"
                                    variant="standard"
                                    onChange={handleAssignedToChange}
                                    defaultValue={assignedTo}
                                    inputRef={assignedToRef}
                                    inputProps={{
                                      maxlength: 100
                                    }}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                  />
                                  {loadingAssignedTo ? (
                                    <LoadingButton loading variant="text">
                                      &nbsp;
                                    </LoadingButton>
                                  ) : (
                                    <Button href="#contained-buttons" onClick={handleAssignedToUpdate}>
                                      Save
                                    </Button>
                                  )}
                                </Stack>
                              )}
                            </Grid>
                          </Grid>
                          <Grid container spacing={2} rowSpacing={4}>
                            <Grid item md={4}>
                              <strong>Last Update:</strong>{' '}
                              {`${handleFormatDate(selectedMilestone.updated_on)} ${handleTimeAmPm(
                                selectedMilestone.updated_on
                              )}`}
                            </Grid>
                            <Grid item md={4}>
                              <strong>PI Name:</strong> <span className="min18H">{selectedMilestone.pi_name} </span>
                            </Grid>
                            <Grid item md={4}>
                              <strong>Milestone Level:</strong>{' '}
                              <span className="min18H">{selectedMilestone.milestone_level}</span>
                            </Grid>
                          </Grid>
                          <br />
                          <Grid container spacing={2}>
                            <Grid item md={12}>
                              <h5>Description:</h5>
                              <p>{selectedMilestone.description}</p>
                              <FormControl fullWidth margin="normal">
                                <TextField
                                  disabled={
                                    selectedMilestone.milestone_status === 'Completed' ||
                                    !selectedMilestone.is_update_eligible
                                  }
                                  id="commentsField"
                                  label="Comments"
                                  variant="standard"
                                  multiline
                                  maxRows={3}
                                  inputRef={commentRef}
                                  defaultValue={selectedMilestone.milestone_comments}
                                  value={comments}
                                  onChange={handleChange2}
                                  inputProps={{
                                    maxlength: 199
                                  }}
                                  sx={{ pr: 1 }}
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      </TabPanel>
                      <TabPanel value={value} index={1}>
                        <Progress selectedMilestone={selectedMilestone} />
                      </TabPanel>
                      <TabPanel value={value} index={2}>
                        <AssociatedDocuments selectedMilestone={selectedMilestone} />
                      </TabPanel>
                      <TabPanel value={value} index={3}>
                        <Box sx={{ position: 'absolute', top: '16px', right: '24px' }}>
                          <Stack
                            direction="row"
                            spacing={2}
                            className="print-actions milestone-audit-print"
                            id="no-print"
                          >
                            <Tooltip title="Print" placement="top" arrow>
                              <IconButton
                                onClick={() => handlePrint()}
                                className="grey-btn"
                                disabled={auditTrailData.length === 0}
                                aria-label="Print this Audit Trial document"
                              >
                                <LocalPrintshopIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download as CSV" placement="top" arrow>
                              <IconButton className="grey-btn" disabled={auditTrailData.length === 0}>
                                <CSVLink
                                  headers={headers}
                                  data={csvDataDownload}
                                  filename={`Milestone_AuditTrail_${moment(new Date()).format(
                                    `${exportDateFormat.toUpperCase()} HH:mm:ss`
                                  )}.csv`}
                                >
                                  <DownloadIcon />
                                </CSVLink>
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>

                        <div className="audit-card">
                          <Box sx={{ bgcolor: 'background.paper', pt: 0 }}>
                            <Box
                              bgcolor="white"
                              sx={{
                                width: '100%',
                                position: 'sticky',
                                top: '0',
                                zIndex: 100,
                                transition: 'top 300ms ease-in-out'
                              }}
                            >
                              <Grid container className="search-block" rowGap={2}>
                                <Grid md={10}>
                                  <FormControl fullWidth variant="standard" sx={{ pr: 2, mt: 2 }}>
                                    <TextField
                                      id="input-with-icon-adornment"
                                      value={filterText}
                                      variant="standard"
                                      placeholder="Search Audits"
                                      autoFocus
                                      onChange={e => handleSearch(e)}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <SearchIcon />
                                          </InputAdornment>
                                        ),
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <span id="group">
                                              <IconButton
                                                aria-label="more"
                                                id="search-filter-IconButton"
                                                aria-controls={openFilterSearch ? 'search-filter' : undefined}
                                                aria-expanded={openFilterSearch ? 'true' : undefined}
                                                aria-haspopup="true"
                                                onClick={handleClickOpenFilterSearch}
                                              >
                                                <TuneIcon />
                                              </IconButton>
                                              {appliedFilterCount > 0 && (
                                                <span className="badge badge-light">{appliedFilterCount}</span>
                                              )}
                                            </span>
                                            <PopupComponent
                                              open={openFilterSearch}
                                              onClose={handleCloseFilterSearch}
                                              BackdropProps={{ invisible: true }}
                                              child
                                            >
                                              <DialogTitle>
                                                <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                                                  Filters
                                                  <Button
                                                    sx={{ mr: 2, position: 'absolute', right: 0 }}
                                                    variant="text"
                                                    href=""
                                                    onClick={handleFilterClear}
                                                  >
                                                    CLEAR
                                                  </Button>
                                                </Typography>
                                              </DialogTitle>
                                              <DialogContent>
                                                <Grid
                                                  container
                                                  direction="row"
                                                  justifyContent="space-between"
                                                  className="filterAH"
                                                >
                                                  <Grid item md={5}>
                                                    <FormControl
                                                      component="fieldset"
                                                      variant="standard"
                                                      margin="normal"
                                                    >
                                                      <FormLabel component="legend" sx={{ mb: 1 }}>
                                                        Action Type
                                                      </FormLabel>
                                                      <FormGroup>
                                                        <Stack direction="column" spacing={1}>
                                                          <FormControlLabel
                                                            sx={{ ml: 0 }}
                                                            control={
                                                              <Switch
                                                                checked={updatedFilterObject.filterProps.all}
                                                                onChange={e => handleActionTypeChange(e, 'All')}
                                                                size="small"
                                                              />
                                                            }
                                                            label="All"
                                                          />
                                                          <FormControlLabel
                                                            control={
                                                              <Switch
                                                                checked={updatedFilterObject.filterProps.dataEntry}
                                                                onChange={e => handleActionTypeChange(e, 'dataEntry')}
                                                                size="small"
                                                              />
                                                            }
                                                            label="Data Entry"
                                                          />
                                                          <FormControlLabel
                                                            control={
                                                              <Switch
                                                                checked={updatedFilterObject.filterProps.dataChange}
                                                                onChange={e => handleActionTypeChange(e, 'dataChange')}
                                                                size="small"
                                                              />
                                                            }
                                                            label="Data Change"
                                                          />

                                                          <FormControlLabel
                                                            control={
                                                              <Switch
                                                                checked={updatedFilterObject.filterProps.fieldQuery}
                                                                onChange={e => handleActionTypeChange(e, 'fieldQuery')}
                                                                size="small"
                                                              />
                                                            }
                                                            label="Field Query"
                                                          />
                                                        </Stack>
                                                      </FormGroup>
                                                    </FormControl>
                                                  </Grid>
                                                  <Grid item md={7}>
                                                    <FormControl fullWidth margin="normal">
                                                      <FormLabel id="" sx={{ mb: 1 }}>
                                                        Date
                                                      </FormLabel>
                                                    </FormControl>
                                                    <Grid container spacing={3} sx={{ mb: 2, display: 'flex' }}>
                                                      <Grid item md={6}>
                                                        <LocalizationProvider
                                                          dateAdapter={AdapterDayjs}
                                                          variant="standard"
                                                          className="custom-datePicker"
                                                        >
                                                          <DatePicker
                                                            label="From"
                                                            inputFormat={userPreferedDateFormat.toUpperCase()}
                                                            value={
                                                              updatedFilterObject.filterFromDate !== null
                                                                ? preferedFormatForDatePicker(
                                                                    updatedFilterObject.filterFromDate?.split(' ')[0]
                                                                  )
                                                                : null
                                                            }
                                                            onChange={(newValue: Date | null) => {
                                                              handleFilterDateChange(newValue, 'filterFromDate');
                                                            }}
                                                            renderInput={(params: any) => (
                                                              <TextField {...params} variant="standard" />
                                                            )}
                                                          />
                                                        </LocalizationProvider>
                                                      </Grid>
                                                      <Grid item md={6}>
                                                        <LocalizationProvider
                                                          dateAdapter={AdapterDayjs}
                                                          variant="standard"
                                                          className="custom-datePicker"
                                                        >
                                                          <DatePicker
                                                            label="To"
                                                            inputFormat={userPreferedDateFormat.toUpperCase()}
                                                            value={
                                                              updatedFilterObject.filterToDate !== null
                                                                ? preferedFormatForDatePicker(
                                                                    updatedFilterObject.filterToDate?.split(' ')[0]
                                                                  )
                                                                : null
                                                            }
                                                            onChange={(newValue: Date | null) => {
                                                              handleFilterDateChange(newValue, 'filterToDate');
                                                            }}
                                                            renderInput={params => (
                                                              <TextField {...params} variant="standard" />
                                                            )}
                                                          />
                                                        </LocalizationProvider>
                                                      </Grid>
                                                    </Grid>

                                                    <FormControl fullWidth margin="normal">
                                                      <FormLabel id="" sx={{ mb: 1 }}>
                                                        User
                                                      </FormLabel>
                                                    </FormControl>
                                                    <FormControl fullWidth variant="standard">
                                                      <Autocomplete
                                                        multiple
                                                        id="filter-select-user"
                                                        options={auditUsersList}
                                                        value={updatedFilterObject.filterUsers}
                                                        onChange={handleUsersChange}
                                                        renderInput={(params: any) => (
                                                          <TextField {...params} variant="standard" label="User(s)" />
                                                        )}
                                                      />
                                                    </FormControl>
                                                  </Grid>
                                                </Grid>
                                              </DialogContent>
                                              <DialogActions>
                                                <Grid container spacing={2}>
                                                  <Grid item md={12} display="flex" justifyContent="flex-end">
                                                    <Stack direction="row" spacing={2}>
                                                      <Button variant="text" href="" onClick={handleFilterApply}>
                                                        Apply
                                                      </Button>
                                                    </Stack>
                                                  </Grid>
                                                </Grid>
                                              </DialogActions>
                                            </PopupComponent>
                                          </InputAdornment>
                                        )
                                      }}
                                    />
                                  </FormControl>
                                </Grid>
                                <Grid md={2}>
                                  <FormControl fullWidth variant="standard" sx={{ mb: 2 }}>
                                    <InputLabel id="demo-simple-select-standard-label">Sort By</InputLabel>
                                    <Select
                                      labelId="demo-simple-select-standard-label"
                                      id="demo-simple-select-standard"
                                      value={sortBy}
                                      onChange={e => {
                                        handleSortbyChange(e);
                                      }}
                                    >
                                      <MenuItem value="Newest">Newest</MenuItem>
                                      <MenuItem value="Oldest">Oldest</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                              </Grid>

                              {auditTrailData.length > 0 && Object.keys(paginatedAuditDetails).length !== 0 && (
                                <Grid container>
                                  <Grid item md={8} />
                                  <Grid
                                    item
                                    id="no-print"
                                    md={4}
                                    sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}
                                  >
                                    <Button onClick={handleClickExpandAll} variant="text">
                                      {getText()}
                                    </Button>
                                  </Grid>
                                </Grid>
                              )}
                            </Box>

                            <Grid container>
                              <Grid md={12} className="audit-trail-list">
                                <Grid container sx={{ '& .milestone-audit-pagination': { left: '57% !important' } }}>
                                  {Object.keys(paginatedAuditDetails).length !== 0 ? (
                                    Object.keys(paginatedAuditDetails).map((item: any, index) => {
                                      return (
                                        <div key={index} className="accordion-result-section">
                                          <Grid md={12}>
                                            <Box sx={{ mb: 2 }}>
                                              <Grid container>
                                                <Grid md={8}>
                                                  <Typography
                                                    variant="h6"
                                                    component="div"
                                                    sx={{ mb: 1 }}
                                                    className="h6-fs16"
                                                  >
                                                    {moment(item).format('MMMM Do, YYYY')}
                                                  </Typography>
                                                </Grid>
                                              </Grid>
                                              {paginatedAuditDetails[item].map((updates: any, index: any) => {
                                                let data = [];
                                                if (updates.reason_data_changed.includes('Query')) {
                                                  data = JSON.parse(updates.changed_data.replace(/'/g, '"'));
                                                }

                                                return !updates.reason_data_changed.includes('Query') ? (
                                                  <Accordion
                                                    key={updates.id}
                                                    expanded={getExpandAndCollapseState(updates.id)}
                                                    onChange={(e, expanded) => {
                                                      handleChangeAccordions(e, expanded, updates.id);
                                                    }}
                                                    className="CustomAccordion-AH"
                                                  >
                                                    <AccordionSummary
                                                      expandIcon={<ExpandMoreIcon />}
                                                      aria-controls="panel1bh-content"
                                                      id="panel1bh-header"
                                                    >
                                                      <Box>
                                                        <Stack
                                                          spacing={1.5}
                                                          direction="row"
                                                          sx={{ alignItems: 'center', mb: 1 }}
                                                        >
                                                          <Typography variant="h6" component="div">
                                                            {`${selectedMilestone.minor_milestone}: ${updates.field_label}`}
                                                          </Typography>
                                                          <Typography variant="body2" gutterBottom>
                                                            {`${handleFormatDate(updates.date_time_of_change)} `}{' '}
                                                            <span>{`${handleTimeAmPm(
                                                              updates.date_time_of_change
                                                            )}`}</span>
                                                          </Typography>
                                                          <Chip
                                                            label={`${updates.reason_data_changed}`}
                                                            color="success"
                                                            size="small"
                                                          />
                                                        </Stack>
                                                        <Typography component="div" gutterBottom variant="body2">
                                                          <strong>Data Changed To:</strong>{' '}
                                                          {(updates.field_changed === 'start_date_adjusted' ||
                                                            updates.field_changed === 'end_date_adjusted' ||
                                                            updates.field_changed === 'start_date_actual' ||
                                                            updates.field_changed === 'end_date_actual' ||
                                                            updates.field_changed === 'start_date_planned_baseline' ||
                                                            updates.field_changed === 'end_date_planned_baseline') &&
                                                          updates.changed_data !== 'None'
                                                            ? handleFormatDate(updates.changed_data)
                                                            : updates.changed_data !== 'None'
                                                            ? updates.changed_data
                                                            : ''}
                                                        </Typography>
                                                        <Typography component="div" gutterBottom variant="body2">
                                                          <strong>Previous Value:</strong>{' '}
                                                          {(updates.field_changed === 'start_date_adjusted' ||
                                                            updates.field_changed === 'end_date_adjusted' ||
                                                            updates.field_changed === 'start_date_actual' ||
                                                            updates.field_changed === 'end_date_actual' ||
                                                            updates.field_changed === 'start_date_planned_baseline' ||
                                                            updates.field_changed === 'end_date_planned_baseline') &&
                                                          updates.data_changed_from !== 'None'
                                                            ? handleFormatDate(updates.data_changed_from)
                                                            : updates.data_changed_from !== 'None'
                                                            ? updates.data_changed_from
                                                            : ''}
                                                        </Typography>
                                                      </Box>
                                                    </AccordionSummary>

                                                    <AccordionDetails>
                                                      <Typography component="div" gutterBottom variant="body2">
                                                        <strong>Field:</strong> {`${updates.field_label}`}
                                                      </Typography>
                                                      <Typography component="div" gutterBottom variant="body2">
                                                        <strong>Entered By:</strong> {`${updates.audit_user_name}`}
                                                      </Typography>
                                                      {/* <Typography component="div" gutterBottom variant="body2">
                                                            <strong>Reason:</strong> 
                                                          </Typography> */}
                                                    </AccordionDetails>
                                                  </Accordion>
                                                ) : (
                                                  <Accordion
                                                    key={updates.id}
                                                    expanded={getExpandAndCollapseState(updates.id)}
                                                    onChange={(e, expanded) => {
                                                      handleChangeAccordions(e, expanded, updates.id);
                                                    }}
                                                    className="CustomAccordion-AH"
                                                  >
                                                    <AccordionSummary
                                                      expandIcon={<ExpandMoreIcon />}
                                                      aria-controls="panel1bh-content"
                                                      id="panel1bh-header"
                                                    >
                                                      <Box>
                                                        <Stack
                                                          spacing={1.5}
                                                          direction="row"
                                                          sx={{ alignItems: 'center', mb: 1 }}
                                                        >
                                                          <Typography variant="h6" component="div">
                                                            {`${selectedMilestone.minor_milestone}: ${
                                                              updates.reason_data_changed.split('-')[1]
                                                            }`}
                                                          </Typography>
                                                          <Typography variant="body2" gutterBottom>
                                                            {`${handleFormatDate(updates.date_time_of_change)} `}{' '}
                                                            <span>{`${handleTimeAmPm(
                                                              updates.date_time_of_change
                                                            )}`}</span>
                                                          </Typography>
                                                          <Chip
                                                            label={`${updates.reason_data_changed.split('-')[0]}`}
                                                            color="success"
                                                            size="small"
                                                          />
                                                        </Stack>
                                                      </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                      <Typography component="div" gutterBottom variant="body2">
                                                        <strong>
                                                          {data.query_response
                                                            ? 'Query Responded'
                                                            : updates.reason_data_changed?.includes('Resolved')
                                                            ? 'Query Resolved'
                                                            : 'Query ID'}
                                                          :
                                                        </strong>{' '}
                                                        {data?.query_id}
                                                      </Typography>
                                                      <Typography component="div" gutterBottom variant="body2">
                                                        <strong>
                                                          {data.report_forward_to
                                                            ? 'Forwarded By'
                                                            : data.query_response
                                                            ? 'Responded By'
                                                            : data.deleted_by
                                                            ? 'Deleted By'
                                                            : updates.reason_data_changed?.includes('Resolved')
                                                            ? 'Resolved By'
                                                            : 'Raised By'}
                                                          :
                                                        </strong>{' '}
                                                        {data.updated_by ||
                                                          data.created_by ||
                                                          data.query_response ||
                                                          data.deleted_by}
                                                      </Typography>
                                                      {data.report_forward_to ||
                                                      data.query_response ||
                                                      data.created_by ? (
                                                        <>
                                                          {!data.query_response ? (
                                                            <Typography component="div" gutterBottom variant="body2">
                                                              <strong>Send To:</strong>{' '}
                                                              {data.report_forward_to || data.report_send_to}
                                                            </Typography>
                                                          ) : (
                                                            ''
                                                          )}
                                                          <Typography component="div" gutterBottom variant="body2">
                                                            <strong>
                                                              {data.query_response ? 'Response' : 'Message'}:
                                                            </strong>{' '}
                                                            {data.report_comment || data.query_response}
                                                          </Typography>
                                                        </>
                                                      ) : (
                                                        ''
                                                      )}
                                                    </AccordionDetails>
                                                  </Accordion>
                                                );
                                              })}
                                            </Box>
                                          </Grid>
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <Typography
                                      key={1}
                                      variant="body1"
                                      gutterBottom
                                      sx={{ textAlign: 'center', color: 'rgba(0,0,0,0.6)', my: 6, width: '100%' }}
                                    >
                                      No audit trail details found for selected milestone/field/filter criteria
                                    </Typography>
                                  )}
                                  {auditTrailData.length > 0 && Object.keys(paginatedAuditDetails).length !== 0 && (
                                    <Stack spacing={2} className="siv-pagination milestone-audit-pagination">
                                      <Pagination
                                        count={Math.ceil(noOfAuditResults / perPage)}
                                        page={page}
                                        onChange={handlePagination}
                                        disabled={paginatedAuditDetails.length === 0}
                                      />
                                    </Stack>
                                  )}
                                </Grid>
                              </Grid>
                            </Grid>
                          </Box>
                        </div>
                      </TabPanel>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item md={12} className="modal-footer-actions" sx={{ px: 3.5 }}>
                <Grid container spacing={1.5} sx={{ pb: 1.5, width: 'auto' }}>
                  <Grid item md={2.4} />
                  <Grid
                    item
                    md={9.6}
                    display="flex"
                    justifyContent="space-between"
                    sx={{ borderTop: '1px solid rgba(0,0,0,0.12)' }}
                  >
                    {value === 0 && (
                      <Stack direction="row" spacing={1.5} sx={{ ml: -1.5 }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!disableHoldBtn}
                          onClick={() => getUpdatedStatus('On hold')}
                        >
                          Set on Hold
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!disableNABtn}
                          onClick={() => getUpdatedStatus('Not applicable')}
                        >
                          Not Applicable
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={
                            !(
                              checkPermission('reactivate_site_milesotne') &&
                              (selectedMilestone.milestone_status === 'On hold' ||
                                selectedMilestone.milestone_status === 'Not applicable')
                            )
                          }
                          onClick={handleReactiveMilestone}
                        >
                          Reactivate
                        </Button>
                      </Stack>
                    )}
                    <Stack direction="row" spacing={2} sx={{ flex: '1 0 auto', justifyContent: 'flex-end' }}>
                      <Button href="#contained-buttons" className="grey-btn" onClick={handleModalClose}>
                        Close
                      </Button>
                      {value !== 3 && (
                        <Button href="#contained-buttons" onClick={() => handleMilestonesDetailsNextBtn(value)}>
                          Next
                        </Button>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>
        </div>
        <div>
          <PopupComponent
            open={openConfirmExit}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="confirm-modal"
            child
          >
            <DialogTitle id="alert-dialog-title">
              <b>Confirm Exit</b>
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">{unSavedWarning}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <ButtonComponent
                onClick={() => {
                  setValue(0);
                  setOpenConfirmExit(false);
                }}
                className="grey-btn"
              >
                CLOSE
              </ButtonComponent>
              <ButtonComponent onClick={() => handleConfirmExitOK()}>OK</ButtonComponent>
            </DialogActions>
          </PopupComponent>
        </div>
      </DialogContent>
    </>
  );
};
